/**
 * DIEUKYDIEU.TV - SPA ROUTER & NAVIGATION SYSTEM
 * Single Page Application view switching, hash routing, browser history back/forward navigation.
 */

import { state } from './state.js';
import { updateCarousel } from './carousel.js';
import { openCategoryBySlug, currentDetailCategory, filteredCategoryArticles, currentCategoryArticles, getSectionsData, getSitemapTree } from './explore.js';
import { openStoryDetail as renderAndOpenStoryDetail, openStoryBySlugOrId, currentStoryArticle } from './story.js';
import { refreshAllChipScrolls } from './chip-scroll.js';
import { updateSidebarActive } from './sidebar.js';

export const navMap = {
    'home-view': 0,
    'category-view': 1,
    'media-view': 2,
    'category-detail-view': 1
};

export function updateNavigationIndicator(viewId) {
    const index = navMap[viewId];
    document.querySelectorAll('[data-nav-target]').forEach((btn) => {
        const target = btn.getAttribute('data-nav-target');
        const isActive = (target === viewId);
        btn.classList.toggle('active', isActive);
        btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    document.querySelectorAll('.nav-container .nav-item').forEach((btn, idx) => {
        if (idx < 3) {
            const isActive = (idx === index);
            btn.classList.toggle('active', isActive);
            btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
        }
    });
}

const viewScrollPositions = new Map();

export function saveCurrentViewScroll() {
    if (typeof window !== 'undefined' && state.currentView) {
        viewScrollPositions.set(state.currentView, window.scrollY);
    }
}

export function navigateToView(newViewId, options = {}) {
    const { pushHistory = true, restoreScroll = false } = options;
    if (newViewId === state.currentView) {
        updateUniversalFloatingBar(newViewId);
        updateSidebarActive(newViewId, currentDetailCategory ? currentDetailCategory.slug : null);
        return;
    }
    const newViewEl = document.getElementById(newViewId);
    if (!newViewEl) return;

    saveCurrentViewScroll();

    if (state.currentView !== 'story-detail-view' && state.currentView !== 'category-detail-view') {
        state.previousView = state.currentView;
    }

    document.querySelectorAll('.view-section').forEach(sec => sec.classList.remove('active'));
    newViewEl.classList.add('active');
    document.body.classList.toggle('story-reader-active', newViewId === 'story-detail-view');

    const targetScrollY = (restoreScroll && viewScrollPositions.has(newViewId))
        ? viewScrollPositions.get(newViewId)
        : 0;
    window.scrollTo({ top: targetScrollY, behavior: 'instant' });

    state.currentView = newViewId;
    updateUniversalFloatingBar(newViewId);
    requestAnimationFrame(() => refreshAllChipScrolls());

    if (newViewId === 'home-view') {
        requestAnimationFrame(() => updateCarousel(false));
    }
    if (navMap[newViewId] !== undefined) {
        updateNavigationIndicator(newViewId);
    }
    updateSidebarActive(newViewId, currentDetailCategory ? currentDetailCategory.slug : null);

    if (pushHistory) {
        let cleanPath = '/';
        if (newViewId === 'category-view') {
            cleanPath = '/kham-pha';
        } else if (newViewId === 'media-view') {
            cleanPath = '/media';
        } else if (newViewId !== 'home-view' && newViewId !== 'story-detail-view' && newViewId !== 'category-detail-view') {
            cleanPath = `/${newViewId.replace('-view', '')}`;
        }
        window.history.pushState({ viewId: newViewId }, '', cleanPath);
    }
}

export function updateUniversalFloatingBar(viewId = state.currentView) {
    const bar = document.getElementById('story-floating-toolbar');
    if (!bar) return;

    if (viewId !== 'story-detail-view') {
        bar.classList.add('hidden-bar');
        return;
    }

    bar.classList.remove('hidden-bar');

    // Contextual button visibility
    const aaBtn = document.getElementById('story-aa-btn');
    const likeBtn = document.getElementById('story-top-like-btn');
    const shareBtn = document.getElementById('story-top-share-btn');
    const popover = document.getElementById('apple-reader-popover');

    const isStoryView = (viewId === 'story-detail-view');

    if (aaBtn) aaBtn.style.display = isStoryView ? 'inline-flex' : 'none';
    if (likeBtn) likeBtn.style.display = isStoryView ? 'inline-flex' : 'none';
    if (shareBtn) shareBtn.style.display = 'inline-flex';
    if (popover && !isStoryView) popover.classList.remove('show');

    // Cập nhật cụm Dynamic Island Capsule ở giữa (Chỉ giữ Text tiêu đề trọn vẹn)
    const capsuleTitle = document.getElementById('story-capsule-title');
    if (!capsuleTitle) return;

    if (viewId === 'category-detail-view') {
        const catName = (currentDetailCategory && currentDetailCategory.name) || 
                        (document.getElementById('cat-detail-heading')?.textContent) || 
                        'Chuyên mục';
        capsuleTitle.textContent = catName;
    } else if (viewId === 'media-view') {
        capsuleTitle.textContent = 'Kho Media & Đa Phương Tiện';
    } else if (viewId === 'story-detail-view') {
        const storyTitle = (currentStoryArticle && currentStoryArticle.title) || 'Bài viết';
        capsuleTitle.textContent = storyTitle;
    } else if (viewId === 'category-view') {
        capsuleTitle.textContent = 'Khám Phá';
    }
}

export function handleUniversalFloatingBack() {
    const current = state.currentView;
    if (current === 'story-detail-view') {
        goBackFromStory();
    } else if (current === 'category-detail-view') {
        if (typeof window !== 'undefined' && typeof window.closeCategoryDetail === 'function') {
            window.closeCategoryDetail();
        } else {
            handleSafeBack('category-view', '/kham-pha');
        }
    } else if (current === 'category-view' || current === 'media-view') {
        switchNav('home-view', 0);
    } else {
        handleSafeBack('home-view', '/');
    }
}

export function handleUniversalShare(event) {
    if (event) {
        if (typeof event.preventDefault === 'function') event.preventDefault();
        if (typeof event.stopPropagation === 'function') event.stopPropagation();
    }
    const current = state.currentView;
    if (current === 'story-detail-view') {
        if (typeof window !== 'undefined' && typeof window.openStoryShare === 'function') {
            window.openStoryShare(event);
            return;
        }
    }
    const title = document.title || 'dieukydieu.tv';
    const url = window.location.href;
    if (typeof window !== 'undefined' && typeof window.openShareAction === 'function') {
        window.openShareAction(title, url, event);
    } else if (navigator.share) {
        navigator.share({ title, url }).catch(() => {});
    }
}

export function switchNav(viewId, navIndex) {
    if (typeof window !== 'undefined' && typeof window.closeMobileSidebar === 'function') {
        window.closeMobileSidebar();
    }
    navigateToView(viewId, { pushHistory: true });
    if (viewId === 'home-view') {
        setTimeout(() => updateCarousel(), 60);
    }
}

export function parseCurrentRoute() {
    if (typeof window === 'undefined') return '';
    if (window.location.hash && window.location.hash !== '#' && window.location.hash !== '#/') {
        return window.location.hash.replace(/^#\/?/, '');
    }
    return window.location.pathname.replace(/^\/+|\/+$/g, '');
}

export function isCategorySlug(slug) {
    if (!slug) return false;
    const clean = String(slug).toLowerCase().trim();
    const sections = getSectionsData() || [];
    if (sections.some(s => s.slug?.toLowerCase() === clean || s.id?.toLowerCase() === clean)) return true;
    const tree = getSitemapTree() || [];
    return tree.some(r => r.slug?.toLowerCase() === clean || (r.children && r.children.some(c => c.slug?.toLowerCase() === clean || (c.subChildren && c.subChildren.some(s => s.slug?.toLowerCase() === clean)))));
}

export function handleHashRoute() {
    const rawRoute = parseCurrentRoute();
    if (!rawRoute || rawRoute === 'home') {
        navigateToView('home-view', { pushHistory: false });
        return;
    }

    if (rawRoute === 'kham-pha' || rawRoute === 'khampha' || rawRoute === 'category') {
        navigateToView('category-view', { pushHistory: false });
    } else if (rawRoute.startsWith('kham-pha/') || rawRoute.startsWith('khampha/') || rawRoute.startsWith('category/')) {
        const slug = rawRoute.replace(/^(kham-pha|khampha|category)\//, '');
        if (isCategorySlug(slug)) {
            openCategoryBySlug(slug, false);
        } else {
            openStoryBySlugOrId(slug, false);
        }
    } else if (rawRoute.startsWith('story/') || rawRoute.startsWith('bai-viet/') || rawRoute.startsWith('article/')) {
        const slugOrId = rawRoute.replace(/^(story|bai-viet|article)\//, '');
        openStoryBySlugOrId(slugOrId, false);
    } else if (rawRoute === 'story-detail') {
        openStoryDetail(currentStoryArticle || 101, null, null, null, null, null, false);
    } else if (rawRoute === 'media') {
        navigateToView('media-view', { pushHistory: false });
    } else if (document.getElementById(`${rawRoute}-view`)) {
        navigateToView(`${rawRoute}-view`, { pushHistory: false });
    }
}

export function handleSafeBack(fallbackView = 'category-view', fallbackPath = '/kham-pha') {
    if (typeof window !== 'undefined' && window.history.length > 1) {
        window.history.back();
    } else {
        navigateToView(fallbackView, { restoreScroll: true });
        if (fallbackPath) {
            window.history.replaceState({ viewId: fallbackView }, '', fallbackPath);
        }
    }
}

export function openStoryDetail(titleOrArticle, imgUrl, desc, tag, duration, id, pushHistory = true) {
    renderAndOpenStoryDetail(titleOrArticle, imgUrl, desc, tag, duration, id, pushHistory);
}

export { openStoryBySlugOrId };

export function goBackFromStory() {
    const progressBar = document.getElementById('reading-progress-bar');
    if (progressBar) {
        progressBar.classList.remove('active');
        progressBar.style.width = '0%';
    }
    if (typeof window !== 'undefined' && typeof window.stopStorySpeech === 'function') {
        window.stopStorySpeech();
    }
    document.body.classList.remove('story-reader-active');
    handleSafeBack(state.previousView || 'category-view', '/kham-pha');
}

export function openTruthArticle(bannerIndex, event) {
    if (event) {
        event.preventDefault();
        event.stopPropagation();
    }
    const bannerUrls = {
        1: "https://mb.dkn.tv/bai-viet-cua-dai-su-ly-hong-chi/nha-sang-lap-phap-luan-cong-cong-bo-bai-viet-vi-sao-co-nhan-loai.html",
        2: "https://mb.dkn.tv/bai-viet-cua-dai-su-ly-hong-chi/nha-sang-lap-phap-luan-cong-cong-bo-bai-viet-tai-sao-can-phai-cuu-do-chung-sinh.html",
        3: "https://mb.dkn.tv/bai-viet-cua-dai-su-ly-hong-chi/nha-sang-lap-phap-luan-cong-cong-bo-bai-viet-vi-sao-xa-hoi-nhan-loai-la-me.html"
    };
    if (bannerUrls[bannerIndex]) {
        window.open(bannerUrls[bannerIndex], '_blank', 'noopener,noreferrer');
    }
}

export function initRouter() {
    window.addEventListener('popstate', (e) => {
        if (e.state && e.state.viewId) {
            if (e.state.viewId === 'category-detail-view' && e.state.slug) {
                openCategoryBySlug(e.state.slug, false);
            } else if (e.state.viewId === 'story-detail-view' && e.state.slug) {
                openStoryBySlugOrId(e.state.slug, false);
            } else {
                navigateToView(e.state.viewId, { pushHistory: false, restoreScroll: true });
            }
        } else {
            handleHashRoute();
        }
    });

    window.addEventListener('hashchange', () => {
        handleHashRoute();
    });

    updateUniversalFloatingBar(state.currentView);
}

